import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
//@ts-ignore
import Select from 'react-select'
//@ts-ignore
import classnames from 'classnames'
import {
  Modal,
  ModalBody,
  ModalHeader,
  FormFeedback,
  Spinner
  // @ts-ignore
} from 'reactstrap'
import {
  IconX,
  IconSearch,
  IconChevronLeft,
  IconChevronRight,
  IconCheck,
  IconPlus,
  IconDeviceDesktop,
  IconUser,
  IconUsers,
  IconCalendarDollar,
  IconStar,
  IconLeaf,
  IconEye,
  IconChartHistogram,
  IconLayoutGrid
} from '@tabler/icons-react'
// @ts-ignore
import { Button, Input, Radio, Avatar, InputGroup, Badge } from 'dsm-react-lib'
import { staticAssetsPath } from '../../../../configs/constants'
import { useSasToken } from '../../../../_hooks/useSasToken'
import { selectThemeColors, getInitials } from '../../../../utility/Utils'
import { useFetch } from '../../../../_hooks/useFetch'
import { useWorkspaceService } from '../../../../_services/WorkspaceService'
import _endpoints from '../../../../_endpoints'
import {
  AppWidgetItem,
  SelectedAppsWidgets,
  buildCategories,
  buildCategoriesFromApi,
  buildSelectionSummary,
  defaultWidgetCatalog,
  filterItems,
  mapApplicationToItem
} from './addAppWidgetCatalog'
import {
  type WorkspaceDetail,
  SERVER_TO_LAYOUT_VARIANT,
  TEAM_ROLE,
  TEAM_STATUS,
  TEMPLATE_TAB,
  TEMPLATE_TYPE,
  WORKSPACE_ITEM_TYPE,
  WORKSPACE_SCOPE,
  type TemplateTab
} from './createWorkspacePayload'
import WidgetsComingSoon from './WidgetsComingSoon'
//@ts-ignore
import '../styles.css'

export type WorkspacePrivacy = 'personal' | 'team'

interface Workspace {
  id: string
  name: string
  icon?: string
  model?: any
  [key: string]: any
}

export interface CentraliTemplate {
  id: string
  name: string
  apps: string[]
  widgets: string[]
  model: any
  /** True when this template comes from GET /api/workspaces/templates (vs a static preset). */
  isServerTemplate?: boolean
  /** Server template items (Application entries carry an applicationId) used to render icon chips. */
  items?: any[]
  /** Server layout enum name (e.g. "TwoColumn"). */
  layout?: string
  includeAliPanel?: boolean
  itemCount?: number
}

export type TemplateSelection =
  | { type: typeof TEMPLATE_TYPE.BLANK }
  | { type: typeof TEMPLATE_TYPE.WORKSPACE; workspace: Workspace }
  | { type: typeof TEMPLATE_TYPE.CENTRALI; preset: CentraliTemplate }

/** The four starting pane arrangements offered on the "Select layout" step. */
export type LayoutVariant = 'single' | 'columns' | 'left-stack' | 'grid'

export interface WorkspaceLayout {
  variant: LayoutVariant
  /** Whether the workspace opens with the Ali panel docked on the right. */
  aliPanel: boolean
}

interface CreateWorkspaceModalProps {
  isOpen: boolean
  toggle: () => void
  workspaceName: string
  isValidName: boolean
  errorMessage: string
  onNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  workspaceList: Workspace[]
  onCreate: (
    template: TemplateSelection,
    privacy: WorkspacePrivacy,
    layout?: WorkspaceLayout,
    teamId?: string | null,
    selectedAppsWidgets?: SelectedAppsWidgets
  ) => void
  /** Max characters allowed for the workspace name (shown in the counter). */
  maxNameLength?: number
  /** 'create' (default) shows the create flow; 'edit' pre-populates from an existing workspace. */
  mode?: 'create' | 'edit'
  /** The workspace detail (from GET /api/workspaces/{id}) used to seed the edit form. */
  editWorkspace?: WorkspaceDetail | null
  /** True while the edit workspace detail is being fetched. */
  isLoadingEdit?: boolean
  /** Submit handler used in edit mode (mirrors onCreate but without a template). */
  onSave?: (
    privacy: WorkspacePrivacy,
    layout: WorkspaceLayout,
    teamId: string | null,
    selectedAppsWidgets: SelectedAppsWidgets
  ) => void
  /**
   * Locks the privacy (workspace type) to a fixed value and disables the other
   * option. Use 'personal' when creating from My space and 'team' from a team
   * page. Ignored in edit mode (type is always locked there).
   */
  lockedPrivacy?: WorkspacePrivacy
  /**
   * Preselects a team in the team dropdown (create mode only). Set to the
   * current team id when launched from a specific team page (e.g. /teams/{id}).
   */
  defaultTeamId?: string | null
}

/** Tab shown on the "Select apps & widget" step. */
type AppWidgetTab = 'apps' | 'widgets'

// Distinct Tabler icons keyed by the widget's `iconName`, with a sensible default.
const WIDGET_ICONS: Record<string, typeof IconLayoutGrid> = {
  calendar: IconCalendarDollar,
  star: IconStar,
  leaf: IconLeaf,
  eye: IconEye,
  chart: IconChartHistogram
}

const getWidgetIcon = (iconName?: string) => (iconName && WIDGET_ICONS[iconName]) || IconLayoutGrid

/**
 * Curated Centrali presets shown under the "Centrali templates" tab (per the M&G
 * Design System spec). These create a workspace pre-named after the user's input;
 * their app/widget chips are illustrative of each preset's contents.
 */
export const centraliWorkspaceTemplates: CentraliTemplate[] = [
  {
    id: 'centrali-smart',
    name: 'Smart Workspace',
    apps: ['SmartDocs', 'SmartFlow', 'SmartData'],
    widgets: [],
    model: []
  },
  {
    id: 'centrali-workspace',
    name: 'Centrali Workspace',
    apps: ['SmartInsights', 'Ali'],
    widgets: ['Watchlist', 'News', 'Market movers', 'Dividend calendar', 'Most used feature'],
    model: []
  },
  {
    id: 'centrali-superstats',
    name: 'SuperStats Workspace',
    apps: ['SmartData', 'SmartInsights'],
    widgets: ['Performance chart', 'Risk metrics', 'Fund flows'],
    model: []
  },
  {
    id: 'centrali-swiss-bonds',
    name: 'Swiss Bonds 2080',
    apps: ['SmartDocs', 'SmartData', 'SmartInsights', 'Ali'],
    widgets: ['Yield curve', 'Bond ladder'],
    model: []
  }
]

/** Extract the app tab names stored inside a workspace's flexlayout model. */
const getWorkspaceAppNames = (workspace: Workspace): string[] => {
  const root = workspace?.model?.layout
  if (!root) return []
  const names: string[] = []
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return
    const children = Array.isArray(node.children) ? node.children : null
    if (children && children.length) {
      children.forEach(walk)
      return
    }
    // Leaf tab node: no children, carries a display name.
    if (node.name) names.push(node.name)
  }
  walk(root)
  return names
}

const cardBaseClasses =
  'c3-template-card relative flex h-[136px] w-full flex-col justify-start gap-[10px] rounded p-1 text-left transition-all cursor-pointer bg-transparent'

/** An app resolved for display on a workspace template card. */
interface WorkspaceApp {
  id: string
  name: string
  icon?: string
}

/**
 * Renders the apps inside a workspace template card as small icon + name pills,
 * with an overflow "+n" pill (hover tooltip) once the count exceeds `max`.
 */
const TemplateAppChips = ({
  apps,
  appendToken,
  max = 3
}: {
  apps: WorkspaceApp[]
  appendToken: (url: string) => string
  max?: number
}) => {
  const pillRef = useRef<HTMLSpanElement>(null)
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number } | null>(null)
  if (!apps.length) return null
  const visible = apps.slice(0, max)
  const hidden = apps.slice(max)
  const overflow = hidden.length

  const openTooltip = () => {
    const rect = pillRef.current?.getBoundingClientRect()
    const halfWidth = rect ? rect.width / 2 : 0
    const centerLeft = rect ? rect.left + halfWidth : 0
    setTooltipPos(rect ? { top: rect.top, left: centerLeft } : { top: 0, left: 0 })
  }
  const closeTooltip = () => setTooltipPos(null)

  const renderIcon = (app: WorkspaceApp, size: 'sm' | 'md' = 'sm') => {
    const box = size === 'sm' ? 'h-[18px] w-[18px]' : 'h-[20px] w-[20px]'
    if (app.icon) {
      return (
        <img
          src={appendToken(app.icon)}
          alt=""
          aria-hidden="true"
          className={`${box} flex-shrink-0 rounded-[5px] object-contain`}
        />
      )
    }
    return (
      <span
        aria-hidden="true"
        className={`${box} flex flex-shrink-0 items-center justify-center rounded-[5px] bg-[var(--surface-muted,rgba(5,90,96,0.08))] text-[9px] font-semibold text-[var(--brand-accent-bold,#055a60)]`}>
        {getInitials(app.name) || 'A'}
      </span>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      {visible.map((app) => (
        <span
          key={app.id}
          className="inline-flex items-center gap-[6px] rounded-full border border-[var(--border-strong)] bg-[var(--surface-bg)] py-[3px] pl-[4px] pr-[11px] text-xs leading-none theme-text-body">
          {renderIcon(app)}
          <span className="line-clamp-1 max-w-[96px] leading-none">{app.name}</span>
        </span>
      ))}
      {overflow > 0 && (
        <span
          ref={pillRef}
          className="relative inline-flex cursor-default items-center self-stretch rounded-full bg-[var(--brand-tint-bg,#e6efef)] px-[10px] py-[3px] text-xs font-semibold leading-none text-[var(--brand-accent-bold,#055a60)]"
          tabIndex={0}
          role="button"
          aria-label={`${overflow} more: ${hidden.map((app) => app.name).join(', ')}`}
          data-testid="template-chips-overflow"
          onMouseEnter={openTooltip}
          onMouseLeave={closeTooltip}
          onFocus={openTooltip}
          onBlur={closeTooltip}>
          +{overflow}
          {tooltipPos !== null &&
            createPortal(
              <div
                role="tooltip"
                data-testid="template-chips-tooltip"
                style={{ top: tooltipPos.top - 8, left: tooltipPos.left }}
                className="pointer-events-none fixed z-[2000] flex w-[280px] -translate-x-1/2 -translate-y-full flex-col gap-[8px] rounded-[4px] border border-[var(--border-strong)] bg-[var(--surface-elevated)] p-[12px] shadow-[0px_8px_24px_-4px_rgba(28,27,31,0.18)]">
                <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--text-subheading)]">
                  Apps
                </span>
                <span className="flex flex-wrap gap-[6px]">
                  {apps.map((app) => (
                    <span
                      key={app.id}
                      className="flex h-[24px] items-center gap-[5px] rounded-full border border-[var(--border-strong)] bg-[var(--surface-bg)] pl-[4px] pr-[12px] text-xs font-normal theme-text-body">
                      {renderIcon(app)}
                      {app.name}
                    </span>
                  ))}
                </span>
              </div>,
              document.body
            )}
        </span>
      )}
    </div>
  )
}

/** A single grey pane placeholder used inside the layout mini-previews. */
const Pane = ({ className = '' }: { className?: string }) => (
  <div
    className={`rounded-[3px] border border-[var(--border-strong,#cfcdd9)] bg-[var(--surface-muted,#d9d9e0)] ${className}`}
  />
)

/** Renders the miniature pane arrangement for a given layout variant. */
const LayoutPreviewDiagram = ({
  variant,
  aliPanel
}: {
  variant: LayoutVariant
  aliPanel: boolean
}) => {
  const { appendToken } = useSasToken()
  const aliIconPath = appendToken(`${staticAssetsPath}/images/icons/ali_icon.svg`)

  let mainArea: React.ReactNode
  switch (variant) {
    case 'columns':
      mainArea = (
        <div className="flex h-full flex-1 gap-[5px]">
          <Pane className="flex-1" />
          <Pane className="flex-1" />
        </div>
      )
      break
    case 'left-stack':
      mainArea = (
        <div className="flex h-full flex-1 gap-[5px]">
          <Pane className="flex-1" />
          <div className="flex flex-1 flex-col gap-[5px]">
            <Pane className="flex-1" />
            <Pane className="flex-1" />
          </div>
        </div>
      )
      break
    case 'grid':
      mainArea = (
        <div className="flex h-full flex-1 flex-col gap-[5px]">
          <div className="flex flex-1 gap-[5px]">
            <Pane className="flex-1" />
            <Pane className="flex-1" />
          </div>
          <div className="flex flex-1 gap-[5px]">
            <Pane className="flex-1" />
            <Pane className="flex-1" />
          </div>
        </div>
      )
      break
    case 'single':
    default:
      mainArea = <Pane className="h-full flex-1" />
      break
  }

  return (
    <div className="flex h-[96px] w-[214px] items-stretch gap-[5px] rounded-[6px] bg-white p-[8px] shadow-[inset_0_0_0_1px_rgb(226,225,232)]">
      {mainArea}
      {aliPanel && (
        <div className="flex w-[34px] flex-shrink-0 items-center justify-center rounded-[3px] border border-[var(--border-strong,#cfcdd9)] bg-[var(--surface-muted,#d9d9e0)] p-[5px]">
          <img src={aliIconPath} alt="Ali panel" className="h-[18px] w-[18px]" />
        </div>
      )}
    </div>
  )
}

const CreateWorkspaceModal: React.FC<CreateWorkspaceModalProps> = ({
  isOpen,
  toggle,
  workspaceName,
  isValidName,
  errorMessage,
  onNameChange,
  workspaceList,
  onCreate,
  maxNameLength = 80,
  mode = 'create',
  editWorkspace = null,
  isLoadingEdit = false,
  onSave,
  lockedPrivacy,
  defaultTeamId
}) => {
  const isEditMode = mode === 'edit'
  const [privacy, setPrivacy] = useState<WorkspacePrivacy>(lockedPrivacy ?? 'personal')
  const [activeTab, setActiveTab] = useState<TemplateTab>(TEMPLATE_TAB.MINE)
  const [selected, setSelected] = useState<TemplateSelection>({ type: TEMPLATE_TYPE.BLANK })
  // Three-step flow: pick a template, pick a starting layout, then pick apps/widgets.
  const [step, setStep] = useState<'template' | 'layout' | 'apps'>('template')
  const [aliPanel, setAliPanel] = useState<boolean>(true)
  const [layoutVariant, setLayoutVariant] = useState<LayoutVariant | null>(null)
  // Team scoping: when the "Team" privacy option is chosen, the workspace is
  // associated with a team selected from this dropdown (mirrors /agents/add-agent).
  const [teams, setTeams] = useState<any[]>([])
  const [teamId, setTeamId] = useState<string | null>(null)
  const [showTeamError, setShowTeamError] = useState<boolean>(false)
  const [isLoadingTeams, setIsLoadingTeams] = useState<boolean>(false)
  const [, fetchTeams] = useFetch()
  // Personal workspaces fetched from the server to use as templates on the "mine" tab.
  const { getWorkspaces, getWorkspaceTemplates } = useWorkspaceService()
  const [personalWorkspaces, setPersonalWorkspaces] = useState<Workspace[]>([])
  const [isFetchingWorkspaces, setIsFetchingWorkspaces] = useState<boolean>(false)
  // System/Centrali templates fetched from GET /api/workspaces/templates.
  const [centraliTemplates, setCentraliTemplates] = useState<CentraliTemplate[]>([])
  const [templatesFetched, setTemplatesFetched] = useState<boolean>(false)

  // "Select apps & widget" step state (mirrors AddAppWidgetModal).
  const { appendToken } = useSasToken()
  const [isFetchingApps, getApps] = useFetch()
  const [appList, setAppList] = useState<AppWidgetItem[]>([])
  const [appCategories, setAppCategories] = useState<string[]>([])
  const [appsFetched, setAppsFetched] = useState<boolean>(false)
  const [appSearchTerm, setAppSearchTerm] = useState<string>('')
  const [appActiveTab, setAppActiveTab] = useState<AppWidgetTab>('apps')
  const [appActiveCategory, setAppActiveCategory] = useState<string>('All')
  const [selectedAppIds, setSelectedAppIds] = useState<Record<string, AppWidgetItem>>({})
  const categoryScrollRef = useRef<HTMLDivElement | null>(null)
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false)
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false)
  // Focused whenever the modal opens (see effect below)
  const workspaceNameInputRef = useRef<HTMLInputElement | null>(null)

  const updateScrollAffordances = () => {
    const el = categoryScrollRef.current
    if (!el) {
      setCanScrollLeft(false)
      setCanScrollRight(false)
      return
    }
    const maxScrollLeft = el.scrollWidth - el.clientWidth
    setCanScrollLeft(el.scrollLeft > 1)
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1)
  }

  // Reset the wizard back to the first step whenever the modal is (re)opened.
  // In edit mode the template can't be changed, so skip straight to the "Select
  // layout" step (the user can still navigate Back to view the disabled templates).
  useEffect(() => {
    if (isOpen) {
      setStep(isEditMode ? 'layout' : 'template')
      setLayoutVariant(null)
      setAliPanel(true)
      setTeamId(null)
      setShowTeamError(false)
      setAppSearchTerm('')
      setAppActiveTab('apps')
      setAppActiveCategory('All')
      setSelectedAppIds({})
      setAppsFetched(false)
      setActiveTab(TEMPLATE_TAB.MINE)
      setTemplatesFetched(false)
      setCentraliTemplates([])
      // Deferred a tick so focus lands after the modal's own open transition/render.
      const focusTimer = setTimeout(() => workspaceNameInputRef.current?.focus(), 0)
      return () => clearTimeout(focusTimer)
    }
  }, [isOpen, isEditMode])

  // In create mode, honour a locked privacy (e.g. 'team' from a team page,
  // 'personal' from My space) so the correct type is preselected each open.
  useEffect(() => {
    if (isOpen && !isEditMode && lockedPrivacy) {
      setPrivacy(lockedPrivacy)
    }
  }, [isOpen, isEditMode, lockedPrivacy])

  // In create mode, preselect the team when launched from a specific team page
  // (e.g. /teams/{id}) so the current team is chosen in the dropdown. Runs after
  // the open-reset effect above, which clears teamId.
  useEffect(() => {
    if (isOpen && !isEditMode && defaultTeamId) {
      setTeamId(defaultTeamId)
    }
  }, [isOpen, isEditMode, defaultTeamId])

  // In edit mode, seed privacy / team / layout / Ali panel from the fetched workspace.
  useEffect(() => {
    if (!isOpen || !isEditMode || !editWorkspace) return
    setPrivacy(editWorkspace.privacy === WORKSPACE_SCOPE.TEAM ? 'team' : 'personal')
    const seedTeamId = editWorkspace.teamExternalId ?? editWorkspace.teamId
    setTeamId(seedTeamId ? String(seedTeamId) : null)
    setLayoutVariant(SERVER_TO_LAYOUT_VARIANT[editWorkspace.layout] || 'single')
    setAliPanel(Boolean(editWorkspace.includeAliPanel))
    // Highlight the workspace actually being edited on the (disabled) template
    // step, rather than always showing "Start blank" as selected.
    setSelected({
      type: TEMPLATE_TYPE.WORKSPACE,
      workspace: { ...editWorkspace, id: String(editWorkspace.id) } as unknown as Workspace
    })
  }, [isOpen, isEditMode, editWorkspace])

  // In edit mode, preselect the workspace's existing apps once the catalogue loads.
  useEffect(() => {
    if (!isOpen || !isEditMode || !editWorkspace || !appsFetched) return
    const wantedIds = new Set(
      (editWorkspace.items || [])
        .filter((item) => item.itemType === WORKSPACE_ITEM_TYPE.APPLICATION)
        .map((item) => String(item.applicationId))
    )
    if (!wantedIds.size) return
    const preselected: Record<string, AppWidgetItem> = {}
    appList.forEach((item) => {
      if (wantedIds.has(String(item.id)) || wantedIds.has(String(item.applicationId))) {
        preselected[item.id] = item
      }
    })
    setSelectedAppIds(preselected)
  }, [isOpen, isEditMode, editWorkspace, appsFetched, appList])

  // When a Centrali template is selected, adopt its layout and Ali-panel settings
  // so the new workspace inherits them (the user can still change them on the
  // layout step).
  useEffect(() => {
    if (!isOpen || isEditMode || selected.type !== TEMPLATE_TYPE.CENTRALI) return
    const preset = selected.preset
    if (preset.layout) setLayoutVariant(SERVER_TO_LAYOUT_VARIANT[preset.layout] || 'single')
    if (typeof preset.includeAliPanel === 'boolean') setAliPanel(preset.includeAliPanel)
  }, [isOpen, isEditMode, selected])

  // Preselect apps carried over from a selected template's `items` (applicationId
  // list) once the app catalogue has loaded, so they show up pre-checked on the
  // "Select apps & widget" step but remain add/removable. Shared by the Centrali-
  // template and "My workspaces"-template preselect effects below.
  const preselectAppsFromItems = (items: any[] | undefined) => {
    const wantedIds = new Set(
      (Array.isArray(items) ? items : [])
        .filter((item: any) => item?.itemType === WORKSPACE_ITEM_TYPE.APPLICATION)
        .map((item: any) => String(item.applicationId))
    )
    const preselected: Record<string, AppWidgetItem> = {}
    appList.forEach((item) => {
      if (wantedIds.has(String(item.id)) || wantedIds.has(String(item.applicationId))) {
        preselected[item.id] = item
      }
    })
    setSelectedAppIds(preselected)
  }

  // Preselect the Centrali template's apps once the catalogue has loaded.
  useEffect(() => {
    if (!isOpen || isEditMode || selected.type !== TEMPLATE_TYPE.CENTRALI || !appsFetched) return
    preselectAppsFromItems(selected.preset.items)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, selected, appsFetched, appList])

  // Preselect a "My workspaces" template's apps once the catalogue has loaded.
  useEffect(() => {
    if (!isOpen || isEditMode || selected.type !== TEMPLATE_TYPE.WORKSPACE || !appsFetched) return
    preselectAppsFromItems(selected.workspace.items)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEditMode, selected, appsFetched, appList])

  // Load the teams the user can create workspaces for (Owner/Contributor, active).
  useEffect(() => {
    if (!isOpen) return
    let isMounted = true
    const loadTeams = async () => {
      setIsLoadingTeams(true)
      try {
        const response = await fetchTeams(
          `${_endpoints.teams.getAllTeams}?skipCount=0&maxResultCount=100`
        )
        if (!isMounted) return
        setTeams(
          (response?.items || []).filter(
            (team: any) =>
              team.userRole !== null &&
              team.userRole !== TEAM_ROLE.MEMBER &&
              team.status === TEAM_STATUS.ACTIVE
          )
        )
      } catch {
        if (isMounted) setTeams([])
      } finally {
        if (isMounted) setIsLoadingTeams(false)
      }
    }
    loadTeams()
    return () => {
      isMounted = false
    }
  }, [isOpen])

  // Load the user's Personal workspaces from the server to offer as templates.
  useEffect(() => {
    if (!isOpen) return
    let isMounted = true
    const loadWorkspaces = async () => {
      setIsFetchingWorkspaces(true)
      try {
        const response = await getWorkspaces(WORKSPACE_SCOPE.PERSONAL)
        if (!isMounted) return
        setPersonalWorkspaces(Array.isArray(response) ? response : [])
      } catch {
        if (isMounted) setPersonalWorkspaces([])
      } finally {
        if (isMounted) setIsFetchingWorkspaces(false)
      }
    }
    loadWorkspaces()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Lazily load the system/Centrali workspace templates the first time the user
  // opens the "Centrali templates" tab.
  useEffect(() => {
    if (!isOpen || activeTab !== TEMPLATE_TAB.CENTRALI || templatesFetched) return
    let isMounted = true
    const loadTemplates = async () => {
      try {
        const response = await getWorkspaceTemplates()
        if (!isMounted) return
        const mapped: CentraliTemplate[] = (Array.isArray(response) ? response : []).map(
          (template: any) => ({
            id: String(template.id),
            name: template.name,
            apps: [],
            widgets: [],
            model: [],
            isServerTemplate: true,
            items: Array.isArray(template.items) ? template.items : [],
            layout: template.layout,
            includeAliPanel: template.includeAliPanel,
            itemCount:
              typeof template.itemCount === 'number'
                ? template.itemCount
                : (template.items || []).length
          })
        )
        setCentraliTemplates(mapped)
      } catch {
        if (isMounted) setCentraliTemplates([])
      } finally {
        if (isMounted) setTemplatesFetched(true)
      }
    }
    loadTemplates()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, templatesFetched])

  // Fetch the app catalogue once the modal opens. It powers both the app icons
  // shown on the "My workspaces" template cards and the "Select apps" step, so
  // it must be available before the user reaches the apps step. Declared after
  // the teams/workspaces effects so those fetches run first.
  useEffect(() => {
    if (!isOpen || appsFetched) return
    let isMounted = true
    const fetchApps = async () => {
      try {
        const url = _endpoints.search.get.concat(_endpoints.search.newArrivalsQuery)
        const response = await getApps(url)
        if (!isMounted) return
        setAppList((response?.applications || []).map(mapApplicationToItem))
        setAppCategories(buildCategoriesFromApi(response?.categories))
      } catch {
        if (isMounted) {
          setAppList([])
          setAppCategories([])
        }
      } finally {
        if (isMounted) setAppsFetched(true)
      }
    }
    fetchApps()
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appsFetched])

  // Server Personal workspaces usable as templates (exclude the special smartflow workspace).
  const myTemplates = useMemo(
    () => (personalWorkspaces || []).filter((workspace) => workspace?.icon !== 'smartflow'),
    [personalWorkspaces]
  )

  // Templates shown under the "Centrali templates" tab: only real server
  // templates from GET /api/workspaces/templates. When none are returned an
  // empty-state message is shown instead of any placeholder presets.
  const centraliCards = centraliTemplates

  // Map applicationId / id -> catalogue item so workspace items (which only carry
  // an applicationId) can be resolved to an app name and icon for the cards.
  const appMap = useMemo(() => {
    const map = new Map<string, AppWidgetItem>()
    appList.forEach((item) => {
      if (item.applicationId !== undefined && item.applicationId !== null) {
        map.set(String(item.applicationId), item)
      }
      if (item.id !== undefined && item.id !== null) map.set(String(item.id), item)
    })
    return map
  }, [appList])

  // Resolve the apps to display on a workspace template card. Prefers the
  // server `items` (applicationId list) resolved via the catalogue; falls back
  // to the flexlayout tab names when a workspace has no server items.
  const resolveWorkspaceApps = (workspace: Workspace): WorkspaceApp[] => {
    const items = Array.isArray(workspace?.items) ? workspace.items : []
    const appItems = items.filter(
      (item: any) =>
        item?.itemType === WORKSPACE_ITEM_TYPE.APPLICATION ||
        (item?.applicationId !== undefined && item?.applicationId !== null)
    )
    if (appItems.length) {
      return appItems.map((item: any, index: number) => {
        const key = String(item.applicationId ?? item.id ?? index)
        const match = appMap.get(key)
        return {
          id: `${key}-${index}`,
          name: match?.name || `App ${key}`,
          icon: match?.icon
        }
      })
    }
    return getWorkspaceAppNames(workspace).map((name, index) => ({
      id: `${name}-${index}`,
      name
    }))
  }

  const canCreate = isValidName && (workspaceName?.trim() ?? '') !== ''

  const isBlankSelected = selected.type === TEMPLATE_TYPE.BLANK
  // Compare ids as strings: the `Workspace`/`CentraliTemplate` interfaces declare
  // `id: string`, but the raw API response (and `editWorkspace.id`, seeded as a
  // stringified WorkspaceDetail id) can disagree on number vs string at runtime -
  // a strict === would silently never match and break the "selected" highlight.
  const isWorkspaceSelected = (id: string) =>
    selected.type === TEMPLATE_TYPE.WORKSPACE && String(selected.workspace.id) === String(id)
  const isCentraliSelected = (id: string) =>
    selected.type === TEMPLATE_TYPE.CENTRALI && String(selected.preset.id) === String(id)

  // Derived data for the "Select apps & widget" step.
  const widgetList = useMemo(
    () => defaultWidgetCatalog.map((widget) => ({ ...widget, type: 'widget' as const })),
    []
  )
  const itemsForTab = appActiveTab === 'apps' ? appList : widgetList
  const appCategoryPills = useMemo(() => {
    if (appActiveTab === 'apps' && appCategories.length > 1) return appCategories
    return buildCategories(itemsForTab)
  }, [appActiveTab, appCategories, itemsForTab])
  const filteredAppItems = useMemo(
    () => filterItems(itemsForTab, { searchTerm: appSearchTerm, category: appActiveCategory }),
    [itemsForTab, appSearchTerm, appActiveCategory]
  )
  const selectedAppItems = Object.values(selectedAppIds)
  const selectedApps = selectedAppItems.filter((item) => item.type === 'app')
  const selectedWidgets = selectedAppItems.filter((item) => item.type === 'widget')

  useEffect(() => {
    updateScrollAffordances()
  }, [appCategoryPills, isOpen, appActiveTab, step])

  const toggleAppSelection = (item: AppWidgetItem) => {
    setSelectedAppIds((prev) => {
      const next = { ...prev }
      if (next[item.id]) {
        delete next[item.id]
      } else {
        next[item.id] = item
      }
      return next
    })
  }

  const clearAppSelection = () => setSelectedAppIds({})

  const scrollCategories = (direction: 'left' | 'right' = 'right') => {
    const offset = direction === 'left' ? -160 : 160
    categoryScrollRef.current?.scrollBy?.({ left: offset, behavior: 'smooth' })
  }

  const handleAppTabChange = (tab: AppWidgetTab) => {
    setAppActiveTab(tab)
    setAppActiveCategory('All')
  }

  const handleNext = () => {
    if (privacy === 'team' && !teamId) {
      setShowTeamError(true)
      return
    }
    if (!canCreate) return
    setStep('layout')
  }

  const handleLayoutNext = () => {
    if (privacy === 'team' && !teamId) {
      setShowTeamError(true)
      return
    }
    if (!canCreate || !layoutVariant) return
    setStep('apps')
  }

  const handleCreate = () => {
    if (!canCreate || !layoutVariant) return
    // Guard against a Team workspace without a selected team; send the user back
    // to the layout step (where the team dropdown is shown) with an error.
    if (privacy === 'team' && !teamId) {
      setShowTeamError(true)
      setStep('layout')
      return
    }
    onCreate(
      selected,
      privacy,
      { variant: layoutVariant, aliPanel },
      privacy === 'team' ? teamId : null,
      { apps: selectedApps, widgets: selectedWidgets }
    )
  }

  const handleSave = () => {
    if (!canCreate || !layoutVariant) return
    onSave?.(privacy, { variant: layoutVariant, aliPanel }, privacy === 'team' ? teamId : null, {
      apps: selectedApps,
      widgets: selectedWidgets
    })
  }

  const handlePrimarySubmit = () => (isEditMode ? handleSave() : handleCreate())

  const teamSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: '44px',
      cursor: state.isDisabled ? 'not-allowed' : base.cursor,
      backgroundColor: state.isDisabled ? 'var(--surface-muted)' : base.backgroundColor
    }),
    option: (base: any) => ({ ...base, padding: '8px 12px' }),
    // Hides the chevron/separator when disabled - otherwise the control still looks
    // expandable even though clicking it can't open the menu or change the value.
    dropdownIndicator: (base: any, state: any) => ({
      ...base,
      display: state.isDisabled ? 'none' : 'flex'
    }),
    indicatorSeparator: (base: any, state: any) => ({
      ...base,
      display: state.isDisabled ? 'none' : base.display
    })
  }

  const selectedClasses =
    'border-2 border-[var(--color-primary,#055a60)] bg-[var(--brand-accent-bg,rgba(5,90,96,0.08))]'
  const unselectedClasses =
    'border border-[var(--border-strong)] hover:border-[var(--color-primary,#055a60)]'

  const renderPrivacyOption = (
    value: WorkspacePrivacy,
    label: string,
    Icon: typeof IconUser,
    iconClass: string
  ) => {
    // In edit mode the type is fixed; in create mode a lockedPrivacy disables the
    // other option so only the context-appropriate type can be chosen.
    const isLocked = isEditMode || (!!lockedPrivacy && value !== lockedPrivacy)
    return (
      <Radio
        id={`workspace-privacy-${value}`}
        name="workspace-privacy"
        value={value}
        variant="default"
        checked={privacy === value}
        disabled={isLocked}
        onChange={() => {
          // The workspace type (Personal/Team) cannot be changed once created,
          // nor overridden when locked to the page's context.
          if (isLocked) return
          setPrivacy(value)
          if (value !== 'team') {
            setShowTeamError(false)
          }
        }}
        data-testid={`privacy-${value}`}
        label={
          <span className="flex items-center gap-[6px] theme-text-heading">
            <Icon size={16} className={iconClass} />
            {label}
          </span>
        }
      />
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      className="create-workspace-modal"
      data-testid="create-workspace-modal">
      <ModalHeader
        className="!flex !items-center !justify-between !border-0 bg-[var(--color-primary)]"
        toggle={toggle}
        data-testid="create-workspace-header"
        close={
          <IconX
            size={20}
            onClick={toggle}
            data-testid="create-workspace-close"
            className="cursor-pointer text-[var(--gray-300)] transition-colors duration-300 ease-in-out hover:text-white"
          />
        }>
        <div className="flex items-center gap-1">
          <IconDeviceDesktop size={20} color="white" />
          <span className="text-white">{isEditMode ? 'Edit workspace' : 'Create workspace'}</span>
        </div>
      </ModalHeader>

      <ModalBody className="theme-surface flex max-h-[75vh] min-h-[500px] flex-col p-0">
        <div className="flex flex-1 flex-col gap-[18px] overflow-y-auto p-[24px]">
          {/* Workspace name */}
          {step !== 'apps' && (
            <div className="flex flex-col gap-[6px]">
              <Input
                ref={workspaceNameInputRef}
                label="Workspace name"
                placeholder="e.g. Global Equities"
                value={workspaceName}
                onChange={onNameChange}
                invalid={!isValidName}
                helperText={isLoadingEdit ? 'Loading workspace…' : errorMessage}
                maxLength={maxNameLength}
                autoFocus
                disabled={isLoadingEdit}
                data-testid="workspace-input"
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') handlePrimarySubmit()
                }}
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => event.target.select()}
              />
            </div>
          )}

          {/* Privacy settings */}
          {step !== 'apps' && (
            <div className="flex flex-col gap-[10px]">
              <span className="text-sm font-semibold theme-text-heading">Privacy settings</span>
              <div className="flex items-center gap-[48px]">
                {renderPrivacyOption('personal', 'Personal', IconUser, 'text-[#E8833A]')}
                {renderPrivacyOption('team', 'Team', IconUsers, 'text-[var(--team-agent)]')}
              </div>
              {isEditMode && (
                <span className="text-xs theme-text-subheading" data-testid="privacy-locked-hint">
                  The workspace type can&apos;t be changed after creation.
                </span>
              )}
              {/* Teams dropdown - shown only when the Team option is selected */}
              {privacy === 'team' && (
                <div className="flex flex-col gap-[6px]" data-testid="workspace-team-select">
                  <Select
                    isClearable
                    // Locked in edit mode, and also when launched from a team page (defaultTeamId is only ever passed there).
                    isDisabled={isEditMode || !!defaultTeamId}
                    isLoading={isLoadingTeams}
                    loadingMessage={() => 'Loading teams…'}
                    name="workspace-teams"
                    classNamePrefix="select"
                    placeholder={isLoadingTeams ? 'Loading teams…' : 'Select team'}
                    theme={selectThemeColors}
                    menuPlacement="auto"
                    styles={teamSelectStyles}
                    options={teams.map((team: any) => ({ label: team.name, value: team.id }))}
                    value={
                      teamId
                        ? teams
                            .map((team: any) => ({ label: team.name, value: team.id }))
                            .find((option: any) => option.value === teamId) || null
                        : null
                    }
                    onChange={(selectedTeam: any) => {
                      setTeamId(selectedTeam?.value ?? null)
                      if (selectedTeam) setShowTeamError(false)
                    }}
                    className={classnames('react-select', {
                      'is-invalid': showTeamError && !teamId
                    })}
                  />
                  {showTeamError && !teamId && (
                    <FormFeedback invalid={1}>Please select a team</FormFeedback>
                  )}
                </div>
              )}
            </div>
          )}

          {step !== 'apps' && <div className="border-t border-[var(--border-strong)]" />}

          {/* Select template */}
          {step === 'template' && (
            <div className="flex flex-col gap-[16px]">
              <div className="flex flex-col gap-[2px]">
                <span className="text-sm font-semibold theme-text-heading">Select template</span>
                <span className="text-sm theme-text-subheading">
                  Start fresh, or build from a template — your own workspaces or a Centrali preset.
                </span>
              </div>

              {/* Tabs */}
              <div className="flex items-center theme-border-default" role="tablist">
                {(
                  [
                    { id: TEMPLATE_TAB.MINE, label: 'My workspaces' },
                    { id: TEMPLATE_TAB.CENTRALI, label: 'Centrali templates' }
                  ] as { id: TemplateTab; label: string }[]
                ).map((tab) => {
                  const active = tab.id === activeTab
                  return (
                    <div
                      key={tab.id}
                      role="tab"
                      tabIndex={0}
                      aria-selected={active}
                      data-testid={`template-tab-${tab.id}`}
                      onClick={() => setActiveTab(tab.id)}
                      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setActiveTab(tab.id)
                        }
                      }}
                      className={`flex h-10 cursor-pointer items-center justify-center gap-2 rounded-none px-8 text-sm font-medium transition-shadow ${
                        active
                          ? 'text-[var(--color-primary)] shadow-[0px_4px_0px_0px_var(--color-primary)]'
                          : 'text-[var(--text-subheading)]'
                      }`}>
                      {tab.label}
                    </div>
                  )
                })}
              </div>

              {/* Card grid */}
              <div className="grid grid-cols-1 gap-[12px] sm:grid-cols-2">
                {/* Start blank — only offered under the "My workspaces" tab. */}
                {activeTab === TEMPLATE_TAB.MINE && (
                  <button
                    type="button"
                    data-testid="template-card-blank"
                    aria-pressed={isBlankSelected}
                    disabled={isEditMode}
                    onClick={() => {
                      if (isEditMode) return
                      setSelected({ type: TEMPLATE_TYPE.BLANK })
                    }}
                    className={`${cardBaseClasses} c3-template-card-blank items-center justify-center text-center disabled:cursor-not-allowed disabled:opacity-50 ${
                      isBlankSelected
                        ? 'c3-template-card-selected'
                        : 'hover:bg-[var(--brand-accent-bg,rgba(5,90,96,0.06))]'
                    }`}>
                    <IconDeviceDesktop size={28} className="text-[var(--color-primary,#055a60)]" />
                    <span className="text-sm font-semibold text-[var(--color-primary,#055a60)]">
                      Start blank
                    </span>
                  </button>
                )}

                {activeTab === TEMPLATE_TAB.MINE &&
                  myTemplates.map((workspace) => {
                    const apps = resolveWorkspaceApps(workspace)
                    const appCount =
                      apps.length ||
                      (typeof workspace.itemCount === 'number' ? workspace.itemCount : 0)
                    const active = isWorkspaceSelected(workspace.id)
                    return (
                      <button
                        key={workspace.id}
                        type="button"
                        data-testid={`template-card-workspace-${workspace.id}`}
                        aria-pressed={active}
                        disabled={isEditMode}
                        onClick={() => {
                          if (isEditMode) return
                          setSelected({ type: TEMPLATE_TYPE.WORKSPACE, workspace })
                        }}
                        className={`${cardBaseClasses} disabled:cursor-not-allowed disabled:opacity-50 ${
                          active ? selectedClasses : unselectedClasses
                        }`}>
                        <span className="line-clamp-1 text-sm font-semibold theme-text-heading">
                          {workspace.name}
                        </span>
                        <span className="text-xs theme-text-subheading">
                          {appCount} {appCount === 1 ? 'app' : 'apps'}
                        </span>
                        <TemplateAppChips apps={apps} appendToken={appendToken} />
                      </button>
                    )
                  })}

                {activeTab === TEMPLATE_TAB.CENTRALI &&
                  centraliCards.map((preset) => {
                    const active = isCentraliSelected(preset.id)
                    const apps = resolveWorkspaceApps(preset as unknown as Workspace)
                    const appCount =
                      apps.length || (typeof preset.itemCount === 'number' ? preset.itemCount : 0)
                    return (
                      <button
                        key={preset.id}
                        type="button"
                        data-testid={`template-card-centrali-${preset.id}`}
                        aria-pressed={active}
                        disabled={isEditMode}
                        onClick={() => {
                          if (isEditMode) return
                          setSelected({ type: TEMPLATE_TYPE.CENTRALI, preset })
                        }}
                        className={`${cardBaseClasses} disabled:cursor-not-allowed disabled:opacity-50 ${
                          active ? selectedClasses : unselectedClasses
                        }`}>
                        <span className="line-clamp-1 text-sm font-semibold theme-text-heading">
                          {preset.name}
                        </span>
                        <span className="text-xs theme-text-subheading">
                          {appCount} {appCount === 1 ? 'app' : 'apps'}
                        </span>
                        <TemplateAppChips apps={apps} appendToken={appendToken} />
                      </button>
                    )
                  })}

                {activeTab === TEMPLATE_TAB.CENTRALI &&
                  centraliCards.length === 0 &&
                  (!templatesFetched ? (
                    <div className="col-span-full flex items-center justify-center py-2 text-sm theme-text-subheading">
                      <Spinner size="sm" className="me-2" /> Loading Centrali templates…
                    </div>
                  ) : (
                    <div
                      data-testid="centrali-templates-empty"
                      className="col-span-full flex items-center justify-center py-2 text-sm theme-text-subheading">
                      No Centrali templates are available yet.
                    </div>
                  ))}

                {activeTab === TEMPLATE_TAB.MINE &&
                  isFetchingWorkspaces &&
                  myTemplates.length === 0 && (
                    <div className="col-span-full flex items-center justify-center py-2 text-sm theme-text-subheading">
                      <Spinner size="sm" className="me-2" /> Loading your workspaces…
                    </div>
                  )}

                {activeTab === TEMPLATE_TAB.MINE &&
                  !isFetchingWorkspaces &&
                  myTemplates.length === 0 && (
                    <div className="col-span-full flex items-center justify-center py-2 text-sm theme-text-subheading">
                      You have no workspaces to use as a template yet.
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Select layout */}
          {step === 'layout' && (
            <div className="flex flex-col gap-[16px]">
              <div className="flex flex-col gap-[2px]">
                <span className="text-sm font-semibold theme-text-heading">Select layout</span>
                <span className="text-sm theme-text-subheading">
                  Choose a starting layout — you can always adjust it later.
                </span>
              </div>

              {/* Ali panel tabs */}
              <div className="flex items-center theme-border-default" role="tablist">
                {(
                  [
                    { id: true, label: 'With Ali panel' },
                    { id: false, label: 'Without Ali panel' }
                  ] as { id: boolean; label: string }[]
                ).map((tab) => {
                  const active = tab.id === aliPanel
                  return (
                    <div
                      key={tab.label}
                      role="tab"
                      tabIndex={0}
                      aria-selected={active}
                      data-testid={`layout-tab-${tab.id ? 'with-ali' : 'without-ali'}`}
                      onClick={() => setAliPanel(tab.id)}
                      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          setAliPanel(tab.id)
                        }
                      }}
                      className={`flex h-10 cursor-pointer items-center justify-center gap-2 rounded-none px-8 text-sm font-medium transition-shadow ${
                        active
                          ? 'text-[var(--color-primary)] shadow-[0px_4px_0px_0px_var(--color-primary)]'
                          : 'text-[var(--text-subheading)]'
                      }`}>
                      {tab.label}
                    </div>
                  )
                })}
              </div>

              {/* Layout cards */}
              <div className="grid grid-cols-1 gap-[12px] sm:grid-cols-2">
                {(
                  [
                    { variant: 'single', label: 'Single pane' },
                    { variant: 'columns', label: 'Two columns' },
                    { variant: 'left-stack', label: 'Split — main + stack' },
                    { variant: 'grid', label: 'Grid — four panes' }
                  ] as { variant: LayoutVariant; label: string }[]
                ).map((option) => {
                  const active = layoutVariant === option.variant
                  return (
                    <button
                      key={option.variant}
                      type="button"
                      data-testid={`layout-card-${option.variant}`}
                      aria-pressed={active}
                      aria-label={option.label}
                      onClick={() => setLayoutVariant(option.variant)}
                      className={`c3-template-card relative flex h-[136px] w-full items-center justify-center rounded p-1 transition-all cursor-pointer bg-transparent ${
                        active ? selectedClasses : unselectedClasses
                      }`}>
                      {active && (
                        <span
                          data-testid={`layout-card-${option.variant}-radio`}
                          className="absolute left-2 top-2 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--color-primary,#055a60)] bg-white">
                          <span className="block h-2.5 w-2.5 rounded-full bg-[var(--color-primary,#055a60)]" />
                        </span>
                      )}
                      <LayoutPreviewDiagram variant={option.variant} aliPanel={aliPanel} />
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Select apps & widget */}
          {step === 'apps' && (
            <div className="flex flex-1 flex-col gap-[16px]">
              <div className="flex flex-col gap-[2px]">
                <span className="text-sm font-semibold theme-text-heading">
                  Select apps & widget
                </span>
                <span className="text-sm theme-text-subheading">
                  Add the apps and widgets you want — you can always add more later.
                </span>
              </div>

              {/* Search */}
              <InputGroup
                value={appSearchTerm}
                addon={<IconSearch size={16} />}
                addonPosition="start"
                addonType="merged"
                placeholder="Search for apps and widgets"
                data-testid="workspace-apps-search"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setAppSearchTerm(event.target.value)
                }
              />

              {/* Category filter */}
              <div className="flex items-center gap-2">
                {canScrollLeft && (
                  <button
                    type="button"
                    aria-label="Scroll categories left"
                    data-testid="workspace-category-scroll-left"
                    onClick={() => scrollCategories('left')}
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-transparent theme-text-heading hover:bg-[var(--item-hover-bg)]">
                    <IconChevronLeft size={16} />
                  </button>
                )}
                <div
                  ref={categoryScrollRef}
                  onScroll={updateScrollAffordances}
                  data-testid="workspace-category-scroll-container"
                  className="flex flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {appCategoryPills.map((category) => {
                    const isActive = category === appActiveCategory
                    return (
                      <Badge
                        key={category}
                        type="pill"
                        size="medium"
                        label={category}
                        data-testid={`workspace-category-${category}`}
                        className={
                          isActive
                            ? 'theme-badge-filter-active whitespace-nowrap'
                            : 'theme-filter-badge-inactive-primary whitespace-nowrap'
                        }
                        onClick={() => setAppActiveCategory(category)}
                      />
                    )
                  })}
                </div>
                {canScrollRight && (
                  <button
                    type="button"
                    aria-label="Scroll categories right"
                    data-testid="workspace-category-scroll"
                    onClick={() => scrollCategories('right')}
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-transparent theme-text-heading hover:bg-[var(--item-hover-bg)]">
                    <IconChevronRight size={16} />
                  </button>
                )}
              </div>

              {/* Tabs */}
              {/* Tabs + selection summary */}
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 theme-border-default">
                <div className="flex items-center" role="tablist">
                  {(['apps', 'widgets'] as AppWidgetTab[]).map((tab) => {
                    const isActive = tab === appActiveTab
                    const count = tab === 'apps' ? appList.length : widgetList.length
                    return (
                      <div
                        key={tab}
                        role="tab"
                        tabIndex={0}
                        aria-selected={isActive}
                        data-testid={`workspace-apps-tab-${tab}`}
                        onClick={() => handleAppTabChange(tab)}
                        onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            handleAppTabChange(tab)
                          }
                        }}
                        className={`flex h-10 cursor-pointer items-center justify-center gap-2 rounded-none px-8 text-sm font-medium capitalize transition-shadow ${
                          isActive
                            ? 'text-[var(--color-primary)] shadow-[0px_4px_0px_0px_var(--color-primary)]'
                            : 'text-[var(--text-subheading)]'
                        }`}>
                        <span>{tab}</span>
                        {tab === 'apps' && (
                          <Badge
                            type="pill"
                            size="small"
                            label={String(count)}
                            className="theme-badge-filter-active font-semibold">
                            {count}
                          </Badge>
                        )}
                      </div>
                    )
                  })}
                </div>
                {selectedAppItems.length > 0 && (
                  <div className="flex items-center gap-1 pb-1">
                    <span
                      className="text-xs theme-text-subheading"
                      data-testid="workspace-apps-selection-summary">
                      {buildSelectionSummary(selectedApps.length, selectedWidgets.length)}
                    </span>
                    <button
                      type="button"
                      data-testid="workspace-apps-clear-selection"
                      onClick={clearAppSelection}
                      className="border-none bg-transparent p-0 text-xs text-[var(--color-primary)] underline">
                      Clear selection
                    </button>
                  </div>
                )}
              </div>

              {/* Grid */}
              <div className="max-h-[320px] flex-1 overflow-y-auto pl-1 pr-1 [scrollbar-color:var(--brand-accent-color)_var(--scrollbar-track)] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--brand-accent-color)] [&::-webkit-scrollbar-track]:my-1 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[var(--scrollbar-track)] [&::-webkit-scrollbar]:w-[8px]">
                {appActiveTab === 'widgets' ? (
                  <WidgetsComingSoon />
                ) : isFetchingApps ? (
                  <div className="flex w-full items-center justify-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : filteredAppItems.length === 0 ? (
                  <div className="flex w-full items-center justify-center py-5">
                    <p className="mb-0 theme-text-subheading">No apps found.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-1">
                    {filteredAppItems.map((item) => {
                      const isSelected = Boolean(selectedAppIds[item.id])
                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-testid={`workspace-app-card-${item.id}`}
                          aria-pressed={isSelected}
                          onClick={() => toggleAppSelection(item)}
                          style={
                            isSelected
                              ? {
                                  borderColor: 'var(--color-primary, rgb(5,90,96))',
                                  boxShadow: 'inset 0 0 0 2px var(--color-primary, rgb(5,90,96))',
                                  backgroundColor: 'var(--brand-accent-bg, rgba(5,90,96,0.08))'
                                }
                              : undefined
                          }
                          className={`relative flex h-[178px] w-full flex-col items-center gap-[10px] rounded border p-3 pt-6 text-center transition-all cursor-pointer bg-transparent ${
                            isSelected
                              ? ''
                              : 'border-[var(--border-strong,rgb(207,205,217))] hover:border-[var(--text-subheading)]'
                          }`}>
                          {isSelected && (
                            <span className="absolute right-[8px] top-[8px] flex h-[18px] w-[18px] items-center justify-center rounded-full bg-[var(--color-primary,rgb(5,90,96))] text-white">
                              <IconCheck size={12} />
                            </span>
                          )}
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center">
                            {item.type === 'app' ? (
                              item.icon ? (
                                <img
                                  src={appendToken(item.icon)}
                                  alt={item.name}
                                  className="h-10 w-10 flex-shrink-0 rounded-[10px] object-contain"
                                />
                              ) : (
                                <Avatar
                                  content={getInitials(item.name) || 'A'}
                                  size="40px"
                                  shape="rounded"
                                />
                              )
                            ) : (
                              <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-[var(--surface-muted,rgba(5,90,96,0.08))] text-[var(--brand-accent-bold,rgb(5,90,96))]">
                                {(() => {
                                  const WidgetIcon = getWidgetIcon(item.iconName)
                                  return <WidgetIcon size={24} stroke={1.5} />
                                })()}
                              </span>
                            )}
                          </div>
                          <div className="flex w-full flex-1 flex-col items-center gap-[4px]">
                            <h5 className="mb-0 line-clamp-1 w-full text-sm font-semibold theme-text-heading">
                              {item.name}
                            </h5>
                            <p className="mb-0 line-clamp-2 caption-text">{item.description}</p>
                          </div>
                          {item.category && (
                            <Badge
                              type="pill"
                              size="small"
                              label={item.category}
                              className="theme-filter-badge-inactive-primary max-w-full truncate">
                              {item.category}
                            </Badge>
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {/* Actions */}
        <div className="flex items-center justify-between gap-2 px-[24px] py-[16px] shadow-[0_-4px_12px_-6px_rgba(28,27,31,0.18)]">
          {step === 'template' && (
            <>
              <Button
                label="Cancel"
                variant="flat"
                color="neutral"
                data-testid="create-workspace-cancel"
                onClick={toggle}
              />
              <Button
                label="Next"
                color="success"
                disabled={!canCreate}
                data-testid="create-workspace-next"
                onClick={handleNext}
              />
            </>
          )}
          {step === 'layout' && (
            <>
              <Button
                label="Back"
                variant="flat"
                color="neutral"
                icon={<IconChevronLeft size={16} />}
                data-testid="create-workspace-back"
                onClick={() => setStep('template')}
              />
              <Button
                label="Next"
                color="success"
                disabled={!canCreate || !layoutVariant}
                data-testid="create-workspace-layout-next"
                onClick={handleLayoutNext}
              />
            </>
          )}
          {step === 'apps' && (
            <>
              <Button
                label="Back"
                variant="flat"
                color="neutral"
                icon={<IconChevronLeft size={16} />}
                data-testid="create-workspace-apps-back"
                onClick={() => setStep('layout')}
              />
              <Button
                label={isEditMode ? 'Save changes' : 'Create workspace'}
                color="success"
                icon={isEditMode ? <IconCheck size={16} /> : <IconPlus size={16} />}
                disabled={!canCreate || !layoutVariant}
                data-testid="create-workspace-submit"
                onClick={handlePrimarySubmit}
              />
            </>
          )}
        </div>
      </ModalBody>
    </Modal>
  )
}

export default CreateWorkspaceModal
